import React from 'react';
import styled from 'styled-components';

interface Props {
  type: 'hunger' | 'experience';
  current: number;
  max: number;
  label: string;
  color?: string;
  showNumbers?: boolean;
  animated?: boolean;
  showMax?: boolean;
}

const StatusBar: React.FC<Props> = ({
  type,
  current,
  max,
  label,
  color,
  showNumbers = true,
  animated = true,
  showMax = true,
}) => {
  const percentage = Math.min(100, Math.max(0, (current / max) * 100));

  // 타입별 기본 색상
  const getColor = () => {
    if (color) return color;

    switch (type) {
      case 'hunger':
        if (percentage > 70) return '#4CAF50'; // 초록
        if (percentage > 30) return '#FF9800'; // 주황
        return '#F44336'; // 빨강
      case 'experience':
        return '#2196F3'; // 파랑
      default:
        return '#9E9E9E'; // 회색
    }
  };

  // 상태에 따른 아이콘
  const getIcon = () => {
    switch (type) {
      case 'hunger':
        if (percentage > 70) return '🍽️';
        if (percentage > 30) return '😋';
        return '😵';
      case 'experience':
        return '✨';
      default:
        return '📊';
    }
  };

  return (
    <Container>
      <LabelRow>
        <Label>
          <Icon>{getIcon()}</Icon>
          {label}
        </Label>
        {showNumbers && (
          <Numbers>
            <Current $type={type}>{current}</Current>
            {showMax && (
              <>
                <Separator>/</Separator>
                <Max>{max}</Max>
              </>
            )}
          </Numbers>
        )}
      </LabelRow>

      <BarContainer>
        <BarBackground>
          <BarFill
            $percentage={percentage}
            $color={getColor()}
            $animated={animated}
          />
          <BarGlow $color={getColor()} $percentage={percentage} />
        </BarBackground>

        {/* 눈금 표시 */}
        {/* <BarMarkers>
          {Array.from({ length: 10 }, (_, i) => (
            <Marker key={i} $active={percentage > (i * 12.5)} />
          ))}
        </BarMarkers> */}
      </BarContainer>
    </Container>
  );
};

// 🎨 스타일 컴포넌트
const Container = styled.div`
  width: 100%;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--textColor);
`;

const Icon = styled.span`
  margin-right: 6px;
  font-size: 16px;
`;

const Numbers = styled.div`
  display: flex;
  align-items: center;
  font-size: 10px;
  font-family: monospace;
`;

const Current = styled.span<{ $type: string }>`
  font-weight: bold;
  color: ${({ $type }) =>
    $type === 'hunger'
      ? '#4CAF50'
      : $type === 'experience'
      ? '#2196F3'
      : '#666'};
`;

const Separator = styled.span`
  margin: 0 4px;
  color: #999;
`;

const Max = styled.span`
  color: #666;
`;

const BarContainer = styled.div`
  position: relative;
  width: 100%;
`;

const BarBackground = styled.div`
  position: relative;
  width: 100%;
  height: 12px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
`;

const BarFill = styled.div<{
  $percentage: number;
  $color: string;
  $animated: boolean;
}>`
  width: ${({ $percentage }) => $percentage}%;
  height: 100%;
  background: linear-gradient(
    90deg,
    ${({ $color }) => $color},
    ${({ $color }) => $color}88
  );
  border-radius: 10px;
  position: relative;

  ${({ $animated }) =>
    $animated &&
    `
    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.3) 0%,
      transparent 100%
    );
    border-radius: 10px 10px 0 0;
  }
`;

const BarGlow = styled.div<{ $color: string; $percentage: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $percentage }) => $percentage}%;
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: 10px;
  opacity: 0.3;
  filter: blur(4px);
  transform: scale(1.1);
  pointer-events: none;
`;

const BarMarkers = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
  pointer-events: none;
`;

const Marker = styled.div<{ $active: boolean }>`
  width: 2px;
  height: 12px;
  background: ${({ $active }) =>
    $active ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 1px;
  transition: background 0.3s ease;
`;

export default StatusBar;
